import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getProjectDetail } from '../../Services/EditableReportService';
import { AdminProjectDetail as ProjectDetail } from '../../Types/EditableReport.types';
import GroupDocumentsCard from '../../Components/Admin/GroupDocumentsCard';

const INTERVENTION_LABELS: Record<string, string> = {
  NEW_CONSTRUCTION: 'Obra Nueva',
  RENOVATION: 'Reforma',
};

const SURFACE_LABELS: Record<string, string> = {
  AREA_LOWER_THAN_50: '< 50m²',
  AREA_BETWEEN_51_AND_100: '51 - 100m²',
  AREA_BETWEEN_101_AND_200: '101 - 200m²',
  AREA_BETWEEN_201_AND_500: '201 - 500m²',
  AREA_GREATER_THAN_500: '> 500m²',
};

const AdminProjectDetail: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [project, setProject] = useState<ProjectDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!projectId) return;
    try {
      setLoading(true);
      setError(null);
      const data = await getProjectDetail(projectId);
      setProject(data);
    } catch (err: any) {
      setError(err.message || 'Error cargando el proyecto');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const architectName = (owner: ProjectDetail['owner']): string => {
    const full = `${owner.firstName ?? ''} ${owner.lastName ?? ''}`.trim();
    return full || owner.username;
  };

  if (loading) {
    return (
      <div className="container mt-4">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="container mt-4">
        <button className="btn btn-outline-secondary mb-3" onClick={() => navigate('/admin/projects')}>
          <i className="bi bi-arrow-left me-2"></i>
          Volver a proyectos
        </button>
        <div className="alert alert-danger">{error || 'Proyecto no encontrado'}</div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <button className="btn btn-outline-secondary mb-3" onClick={() => navigate('/admin/projects')}>
        <i className="bi bi-arrow-left me-2"></i>
        Volver a proyectos
      </button>

      {/* General info */}
      <div className="card mb-4">
        <div className="card-body">
          <h2 className="mb-1">
            <i className="bi bi-folder2-open me-2"></i>
            {project.externalName}
          </h2>
          {project.internalName && project.internalName !== project.externalName && (
            <p className="text-muted mb-3">{project.internalName}</p>
          )}
          <div className="row g-3">
            <div className="col-md-3 col-6">
              <div className="text-muted small text-uppercase">Cliente</div>
              <div>{project.clientEmail}</div>
            </div>
            <div className="col-md-3 col-6">
              <div className="text-muted small text-uppercase">Arquitecto</div>
              <div>{architectName(project.owner)}</div>
            </div>
            <div className="col-md-3 col-6">
              <div className="text-muted small text-uppercase">Intervención</div>
              <div>{INTERVENTION_LABELS[project.interventionType] || project.interventionType}</div>
            </div>
            <div className="col-md-3 col-6">
              <div className="text-muted small text-uppercase">Superficie</div>
              <div>{SURFACE_LABELS[project.surface] || project.surface}</div>
            </div>
            <div className="col-md-3 col-6">
              <div className="text-muted small text-uppercase">Creado</div>
              <div>{new Date(project.createdAt).toLocaleDateString('es-ES')}</div>
            </div>
            {project.members.length > 0 && (
              <div className="col-md-9 col-12">
                <div className="text-muted small text-uppercase">Miembros</div>
                <div>
                  {project.members.map((m, i) => (
                    <span key={m.id || i} className="badge bg-light text-dark border me-1 mb-1">
                      {m.name}
                      {m.isChild ? ' 👶' : ''}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button type="button" className="btn-close" onClick={() => setError(null)}></button>
        </div>
      )}

      {/* Reports per group */}
      <h4 className="mb-3">
        <i className="bi bi-files me-2"></i>
        Informes por grupo
      </h4>
      {project.groups.length === 0 ? (
        <div className="text-muted">Este proyecto no tiene grupos.</div>
      ) : (
        <div className="d-flex flex-column gap-3">
          {project.groups.map((group) => (
            <GroupDocumentsCard
              key={group.projectGroupId}
              projectId={project.id}
              projectName={project.externalName}
              group={group}
              onChanged={load}
              onError={(msg) => setError(msg || null)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminProjectDetail;
